import { registerIntegration } from "@p0security/shared/integrations/registry";
import {
  type AnyFilterSchema,
  Registration,
} from "@p0security/shared/integrations/types";

import { ClickHouseAccesses } from "./accesses.ts";
import { ClickHouseComponents } from "./components.ts";

// Rule filter values compile to regular expressions, so a rule can say, e.g.,
// that `write` on any table in a `prod` service needs approval. `object` holds
// the requested object only: a view's grant covers the view alone.
const ClickHouseFilters = {
  organization: { keys: ["name"] },
  service: { keys: ["name", "id"] },
  level: { keys: ["name"] },
  object: { keys: ["name"] },
  consoleRole: { keys: ["name"] },
} as const satisfies AnyFilterSchema;
export type ClickHouseFilters = typeof ClickHouseFilters;

// Keyed by component, so a new component doesn't compile until it has one.
// The new item's form labels an item's id with its component's kind, then
// this: "Connector ID", "Organization ID".
const IDENTIFIERS: Record<keyof typeof ClickHouseComponents, string> = {
  connector: "ID",
  organization: "ID",
};
// `Registration` can hand `identifier` a string that isn't a component key.
const identifiers: Partial<Record<string, string>> = IDENTIFIERS;

export const ClickHouseRegistration = Registration({
  accesses: ClickHouseAccesses,
  category: "resource",
  components: ClickHouseComponents,
  docs: { install: "clickhouse", request: "clickhouse/requesting-access" },
  filters: ClickHouseFilters,
  identifier: (component) => identifiers[component] ?? component,
  // Each component is named for what one of its items is.
  itemKind: (component) => component,
  label: "ClickHouse Cloud",
  logoUrl: "https://clickhouse.com/favicon.ico",
  maturity: "beta",
  requestType: "requestable",
  useMapping: [{ component: "organization", use: "iam_write" }],
});

registerIntegration("clickhouse", ClickHouseRegistration);
